<script setup>
    import { useSitesStore } from '@/stores/sites';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { useEventsStore } from '@/stores/events';
    import { updateHead } from '@/composables/helpers/updateHead';
    import { useEventDay } from '@/composables/eventDay';
    import { detailTitle, eventJsonLd, canonicalUrl, seoDescription, shareImage } from '@/utils/seo';

    const route = useRoute();

    // Al: switching to store because of CORS error
    const eventsStore = useEventsStore();
    const eventData = ref();
    const pathId = route.params.event[0];
    
    eventData.value = await eventsStore.fetchEventById(pathId, false);

    // A real 404 for an id that isn't an event, instead of an empty 200 (a
    // soft 404 to search engines). Only when the events list itself loaded and
    // lacks the id too, so a failed fetch can't 404 a real event.
    if (import.meta.server && !eventData.value) {
        const records = eventsStore.eventsData?.records || [];
        if (records.length && !records.some((record) => record?.id === pathId)) {
            throw createError({ statusCode: 404, statusMessage: 'Event not found', fatal: true });
        }
    }

    // Site Data
    const sitesStore = useSitesStore();
    const sitesMap = sitesStore.sitesMap;
    const siteData = computed(() => {
        if (eventData?.value?.fields?.Site && sitesMap) {
            const siteId = eventData?.value?.fields?.Site[0];
            return siteId && sitesMap[siteId] ? sitesMap[siteId] : false;
        }
        return false;
    });

    // Neighborhood Data
    const neighborhoodsStore = useNeighborhoodsStore();
    const neighborhoodsMap = neighborhoodsStore.neighborhoodsMap;
    const neighborhoodData = computed(() => {
        // siteData.value could be false or an actual site object
        if (!siteData?.value || siteData.value === false || !neighborhoodsMap) {
            return false;
        }
        const neighborhoodID = siteData.value?.fields?.['OHC Neighborhood']?.[0];
        return neighborhoodID && neighborhoodsMap[neighborhoodID] ? neighborhoodsMap[neighborhoodID] : false;
    });

    const eventDay = computed(() => {
        if (eventData?.value) return useEventDay(eventData?.value)?.eventDay;
        else return false;
    });

    const membersOnly = computed(() => {
        return eventData?.value?.fields?.['Members Only'] ? eventData?.value?.fields?.['Members Only'] : false;
    });

    const formattedEvent = computed(() => {
        let item = eventData?.value;
        if (eventData?.value && siteData?.value) {
            const loc = siteData?.value?.fields?.Location;
            item.fields.Location = loc;
        }
        if (siteData?.value?.fields?.['Site Address']) {
            const address = siteData?.value?.fields?.['Site Address'];
            item.fields['Site Address'] = address;
        }
        return item;
    });

    const desc = computed(() => {
        return eventData?.value?.fields['Description'] || '';
    });

    // The description was `Description.slice(0, 150)` of raw HTML, so it
    // opened "<p>Can't believe" and stopped mid-word. Now plain text cut at a
    // word boundary. The share image is the event's own photo when it has one.
    updateHead(() => {
        const name = eventData.value?.fields?.['Program Title'];
        const image = shareImage(eventData.value?.fields?.['Photos'], name);
        const jsonLd = eventData.value ? eventJsonLd(eventData.value, siteData.value, { url: canonicalUrl(route.path), image }) : null;
        return {
            title: detailTitle(name),
            description: seoDescription(desc.value),
            path: route.path,
            image: image.url,
            imageWidth: image.width,
            imageHeight: image.height,
            imageAlt: image.alt,
            jsonLd,
        };
    });

</script>

<template>
    <section>
        <article class="event-detail" v-if="eventData" :class="{ 'has-imagery': eventData?.fields['Photos'] && eventData?.fields['Photos']?.length > 0 }">
            <BlocksImageryHero v-if="eventData?.fields['Photos'] && eventData?.fields['Photos']?.length > 0" class="imagery" :photos="eventData?.fields['Photos']" />
            <div class="hd-container">
                <section class="header-details container">
                    <ClientOnly>
                        <p class="date uppercase card-header" v-if="eventDay?.label && eventDay?.date">{{ eventDay?.label }}, Oct. {{ eventDay?.date }}</p>
                    </ClientOnly>
                    <div class="tag-with-time">
                        <!-- Waiting for Members Only Column -->
                        <SvgsMembersOnly v-if="membersOnly" class="member-tag" />
                        <p class="time-text">{{ eventData.fields?.Time }}</p>
                    </div>
                    <h1 class="headline-1">{{ eventData.fields['Program Title'] }}</h1>
                    <p class="gray-tag" v-if="neighborhoodData?.fields?.Name">
                        {{ neighborhoodData.fields.Name }}
                    </p>

                    <div class="marker-with-venue">
                        <SvgsThinMarker class="marker" />
                        <div class="location">
                            <p class="small-heavy">{{ siteData?.fields?.['Site'] }}</p>
                            <p class="body-small address">{{ siteData?.fields?.['Site Address'] }}</p>
                        </div>
                    </div>

                    <ul class="types" v-if="eventData.fields['Event Type']">
                        <li class="turquoise-tag">{{ eventData.fields['Event Type'] }}</li>
                    </ul>
                </section>

                <!-- Waiting for Members Only Column -->
                <BlocksMemberOnlyAccess v-if="membersOnly" />

                <GlobalActions
                    class="actions"
                    v-if="formattedEvent"
                    :item="formattedEvent"
                    useDirectionsSingle
                    :useDirections="false"
                    useShare
                    :useItinerary="false"
                    useFavorite
                    list="ohcFavoriteEvents" />

                <BlocksDetailDescription class="details" v-if="eventData?.fields?.Description">
                    <p class="copy body-book" v-html="eventData.fields.Description"></p>
                </BlocksDetailDescription>

                <div class="related-cards">
                    <div class="container site-info" v-if="siteData">
                        <h2 class="subhead-2">More About this ohc site</h2>
                        <CardsSite :item="siteData" type="large" />
                    </div>

                    <div class="container neighborhood" v-if="neighborhoodData">
                        <h2 class="subhead-2">Explore the Neighborhood Area</h2>
                        <CardsNeighborhood :item="neighborhoodData" />
                    </div>
                </div>

            </div>
        </article>
    </section>
</template>

<style lang="scss">
    .event-detail {
        
        position: relative;

        // 18px under the hero, as Templates/Sites/single.vue sets on its own
        // .header-details — these two pages should read as the same layout.
        .imagery + .header-details {
            padding-top: 18px;
        }

        &:not(.has-imagery) {
            .header-details {
                padding-top: 80px;
            }
        }

        .header-details {
            padding-top: 20px;

            .tag-with-time {
                margin-top: 6px;
                display: flex;
                align-items: center;
                .member-tag {
                    margin-right: 15px;
                }
            }

            h1 {
                margin-top: 8px;
            }
        }

        .marker-with-venue {
            display: flex;

            .location {
                margin-left: 8px;
                .small-heavy {
                    letter-spacing: 0;
                    text-transform: none;
                }

                @media (min-width: $tablet) {
                    p {
                        font-size: 15px;
                        line-height: 16px;
                    }
                }
            }
        }

        .types {
            margin-top: 16px;
        }

        .turquoise-tag {
            margin: 8px 8px 0 0;
        }

        .gray-tag {
            margin-top: 10px;
        }

        .address {
            margin-top: 4px;
        }

        // Vertical rhythm lifted from Templates/Sites/single.vue so the two
        // detail pages stack identically: 32px from the header block to the
        // actions, 60px between major sections, and 20px under a section
        // heading. Was 20 / 40 / 25 here, which is why they read differently.
        .global-actions {
            margin-top: 32px;
        }

        .details {
            margin-top: 60px;
        }

        .site-info,
        .neighborhood {
            margin-top: 60px;

            h2 {
                margin-bottom: 20px;
            }
        }

        // Side by side on desktop rather than stacked (Asana 1217333501985922).
        // auto-fit rather than a fixed 1fr 1fr so an event with only one of the
        // two cards still fills the row instead of leaving a dead column.
        .related-cards {
            // Last block on the page, and it had no bottom spacing at all, so it
            // ran flush into the red newsletter banner — measured 0px against the
            // 32/60/60px the blocks above it are separated by (Asana O2-69).
            // Matches that rhythm rather than inventing a new value.
            margin-bottom: 60px;

            @media (min-width: $desktop) {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                gap: 32px;
                align-items: start;

                // Both cards size their image to 50% width on desktop, which
                // assumed a card spanning the full page. Side by side that left
                // each image at a quarter of the row, reading as undersized
                // (Asana 1217515708372032). Fill the column instead, at the
                // same aspect ratio each card already used: the site card was
                // 50% wide by 30% tall, the neighborhood 50% by 28%.
                //
                // Only when the two actually share the row. An event with just
                // one of them gets a single full-width column, where filling it
                // blew the image up to 1150x690 — the site detail page shows
                // this card at 575px, and :only-child keeps that.
                .site-info:not(:only-child) .card-site.large .image-wrapper {
                    width: 100%;
                    padding-top: 60%;
                }

                .neighborhood:not(:only-child) .card-neighborhood .image-wrapper {
                    width: 100%;
                    padding-top: 56%;
                }
            }
        }

        .body-book {
            a {
                color: $red;
                text-decoration: underline;

                &:hover {
                    color: $turquoise;
                }
            }
        }

        @media (min-width: $tablet) {
            width: 100%;
            max-width: $hd;
            margin: auto;

            .cta-large {
                width: 250px;
                margin: 20px auto 150px;
            }

            .member-tag {
                width: 110px;
                height: 25px;
            }

            .header-details .date {
                font-size: 22px;
            }
        }
    }
</style>
